import {
  Ref,
  forwardRef,
  ReactNode,
  ComponentPropsWithoutRef,
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
  FocusEvent,
  useMemo,
} from "react";
import { LayoutUtilProps } from "../../types";
import { ListboxOption } from "./ListboxOption";
import { ListboxContext, ListboxContextProps } from "./internal/ListboxContext";
import { onListboxKeyDown, scrollToOption } from "./internal/utils";
import { useMergeRefs } from "../../hooks";
import { ListboxOptionGroup } from "./ListboxOptionGroup";

import cx from "classnames";
import styles from "./Listbox.module.scss";
import { useLayoutPropsUtil, useTypeAhead } from "../../internal/hooks";

/**
 * Represents an item in the listbox with a label and optional disabled state.
 * @template T - The type of additional properties for the item
 */
export type ListboxItemType<T> = T & {
  /** The display label for the item */
  label: string;
  /** Whether the item is disabled and cannot be selected */
  disabled?: boolean;
};

/**
 * Props for single selection mode without items array
 */
type SingleWithoutItems = {
  /** Selection mode for single item selection */
  selectionMode?: "single";
  /** Whether to disable automatic selection when focusing on an option */
  disableAutoSelectOnFocus?: boolean;
  /** Currently selected item (controlled) */
  selected?: string;
  /** Default selected item (uncontrolled) */
  defaultSelected?: string;
  /** Callback when selection changes */
  onSelectionChange?: (selected: string | undefined) => void;
  /** Items array - not allowed in this mode */
  items?: never;
  /** Child elements to render */
  children: ReactNode;
};

/**
 * Props for multiple selection mode without items array
 */
type MultipleWithoutItems = {
  /** Selection mode for multiple item selection */
  selectionMode: "multiple";
  /** Auto-select on focus is not available in multiple mode */
  disableAutoSelectOnFocus?: never;
  /** Currently selected items (controlled) */
  selected?: string[];
  /** Default selected items (uncontrolled) */
  defaultSelected?: string[];
  /** Callback when selection changes */
  onSelectionChange?: (selected: string[] | undefined) => void;
  /** Items array - not allowed in this mode */
  items?: never;
  /** Child elements to render */
  children: ReactNode;
};

/**
 * Props for single selection mode with items array
 * @template T - The type of additional properties for items
 */
type SingleWithItems<T> = {
  /** Selection mode for single item selection */
  selectionMode?: "single";
  /** Whether to disable automatic selection when focusing on an option */
  disableAutoSelectOnFocus?: boolean;
  /** Currently selected item (controlled) */
  selected?: ListboxItemType<T>;
  /** Default selected item (uncontrolled) */
  defaultSelected?: ListboxItemType<T>;
  /** Callback when selection changes */
  onSelectionChange?: (selected: ListboxItemType<T> | undefined) => void;
  /** Array of items to render */
  items: ListboxItemType<T>[];
  /** Optional render function for items */
  children?: ({ items }: { items: ListboxItemType<T>[] }) => ReactNode;
};

/**
 * Props for multiple selection mode with items array
 * @template T - The type of additional properties for items
 */
type MultipleWithItems<T> = {
  /** Selection mode for multiple item selection */
  selectionMode?: "multiple";
  /** Auto-select on focus is not available in multiple mode */
  disableAutoSelectOnFocus?: never;
  /** Currently selected items (controlled) */
  selected?: ListboxItemType<T>[];
  /** Default selected items (uncontrolled) */
  defaultSelected?: ListboxItemType<T>[];
  /** Callback when selection changes */
  onSelectionChange?: (selected: ListboxItemType<T>[] | undefined) => void;
  /** Array of items to render */
  items: ListboxItemType<T>[];
  /** Optional render function for items */
  children?: ({ items }: { items: ListboxItemType<T>[] }) => ReactNode;
};

/**
 * Props for the Listbox component
 * @template T - The type of additional properties for items
 * @extends ComponentPropsWithoutRef<"ul">
 * @extends LayoutUtilProps
 */
export type ListboxProps<T> = Omit<ComponentPropsWithoutRef<"ul">, "children"> &
  LayoutUtilProps &
  (
    | SingleWithoutItems
    | MultipleWithoutItems
    | SingleWithItems<T>
    | MultipleWithItems<T>
  );

function ListboxInner<T>(props: ListboxProps<T>, ref: Ref<HTMLUListElement>) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    selectionMode = "single",
    items,
    children,
    onKeyDown,
    onFocus,
    onSelectionChange,
    selected: selectedProp,
    defaultSelected,
    disableAutoSelectOnFocus,
    style,
    ...rest
  } = componentProps;

  const listboxRef = useRef<HTMLUListElement>(null);
  const [selected, setSelected] = useState<ListboxProps<T>["defaultSelected"]>(
    selectedProp || defaultSelected,
  );
  const [options, setOptions] = useState<NodeListOf<HTMLElement>>();
  const [typeAhead, updateTypeAhead] = useTypeAhead();
  const [currentFocus, setCurrentFocus] = useState<
    ListboxContextProps<T>["currentFocus"]
  >(
    Array.isArray(selected)
      ? typeof selected[0] === "object"
        ? (selected as ListboxItemType<T>[])[0].label
        : (selected as string[])[0]
      : typeof selected === "object"
        ? (selected as ListboxItemType<T>)?.label
        : (selected as string),
  );

  const ListboxClassNames = cx(styles["listbox"], className, {});

  const controlled = selectedProp !== undefined;

  useEffect(() => {
    if (listboxRef.current) {
      setOptions(
        listboxRef.current.querySelectorAll(
          "[role=option]:not([aria-disabled='true'])",
        ),
      );
    }
  }, [items, listboxRef, selectionMode]);

  useEffect(
    () => {
      if ("selected" in componentProps) {
        setSelected(selectedProp);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedProp],
  );

  useEffect(() => {
    if (!typeAhead.length) {
      return;
    }
    const lowerTypeAhead = typeAhead.toLowerCase();
    const nodes =
      options &&
      Array.from(options).filter((node) =>
        node.innerText.toLowerCase().startsWith(lowerTypeAhead),
      );

    if (!nodes?.length) {
      return;
    }

    let node = nodes[0];

    if (lowerTypeAhead.length === 1 && nodes.length > 1) {
      // Select next matching item on first key press if the list of matches contains our current selection WITH looping
      const focusIndex = currentFocus
        ? nodes.map((node) => node.id).indexOf(currentFocus)
        : -1;
      if (focusIndex > -1) {
        let nextFocusIndex = focusIndex + 1;
        if (nextFocusIndex === nodes.length) {
          nextFocusIndex = 0;
        }
        node = nodes[nextFocusIndex];
      }
    }

    setCurrentFocus(node.id);
    scrollToOption(node);

    if (controlled) return;

    if (selectionMode === "single") {
      if (items) {
        if (disableAutoSelectOnFocus) {
          const focusedItem = items.find(
            (item) => (item as ListboxItemType<T>).label === currentFocus,
          );
          setSelected?.(focusedItem);
          onSelectionChange?.(focusedItem);
        }
        return;
      }

      if (!items) {
        if (disableAutoSelectOnFocus) {
          setSelected(node.id);
          onSelectionChange?.(node.id);
        }
        return;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeAhead]);

  const onKeyDownHandler = (e: KeyboardEvent<HTMLUListElement>) => {
    onKeyDown?.(e);

    if (new RegExp(/^[a-zA-Z0-9]$/i).test(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      updateTypeAhead(e.key);
      return;
    }

    onListboxKeyDown(e, {
      setCurrentFocus,
      currentFocus,
      setSelected,
      selected,
      selectionMode,
      options,
      onSelectionChange,
      disableAutoSelectOnFocus: !!disableAutoSelectOnFocus,
      items,
    });
  };

  const onFocusHandler = (e: FocusEvent<HTMLUListElement>) => {
    onFocus?.(e);

    if (!options) return;

    if (selectionMode === "single") {
      const currentFocusExists = Array.from(options).some(
        (item) => item.id === currentFocus,
      );

      if (currentFocus && currentFocusExists) return;
      if (!items) {
        if (currentFocusExists) {
          setCurrentFocus(selected as string);
          return;
        }
        setCurrentFocus(options[0].id);
        return;
      }
      if (currentFocusExists) {
        setCurrentFocus((selected as ListboxItemType<T>)?.label);
        return;
      }
      setCurrentFocus(items?.[0].label);
      return;
    }

    if (selectionMode === "multiple") {
      const selectedArr = Array.isArray(selected) ? selected : [selected];
      const currentFocusExists = Array.from(options).some(
        (item) => item.id === currentFocus,
      );

      if (currentFocus && currentFocusExists) return;
      if (!items) {
        console.log({ currentFocus, currentFocusExists });
        if (currentFocusExists) {
          setCurrentFocus((selectedArr as ListboxItemType<T>[])[0].label);
          return;
        }
        setCurrentFocus(options[0].id);
        return;
      }
      if (currentFocusExists) {
        setCurrentFocus((selectedArr as ListboxItemType<T>[])[0]?.label);
        return;
      }
      setCurrentFocus(items?.[0].label);
      return;
    }

    setCurrentFocus(options[0].id);
  };

  const mergeRef = useMergeRefs([ref, listboxRef]);

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  const value = useMemo(
    () => ({
      setCurrentFocus,
      currentFocus,
      setSelected,
      selected,
      selectionMode,
      items,
      options,
      onSelectionChange,
      disableAutoSelectOnFocus: !!disableAutoSelectOnFocus,
      controlled,
    }),
    [
      currentFocus,
      disableAutoSelectOnFocus,
      onSelectionChange,
      options,
      selected,
      selectionMode,
      items,
      controlled,
    ],
  );

  return (
    <ListboxContext.Provider value={value}>
      <ul
        tabIndex={0}
        role="listbox"
        style={styleCombined}
        aria-multiselectable={selectionMode === "multiple" ? "true" : "false"}
        aria-activedescendant={currentFocus}
        className={ListboxClassNames}
        data-anv="listbox"
        onKeyDown={onKeyDownHandler}
        onFocus={onFocusHandler}
        {...rest}
        ref={mergeRef}
      >
        {items ? children?.({ items }) : children}
      </ul>
    </ListboxContext.Provider>
  );
}

ListboxInner.displayName = "Listbox";

/**
 * Listbox component for selecting one or more items from a list
 *
 * Features:
 * - Supports single and multiple selection modes
 * - Keyboard navigation with arrow keys and type-ahead
 * - Optional auto-selection on focus
 * - Controlled and uncontrolled modes
 * - Accessible with proper ARIA attributes
 * - Supports custom item rendering
 * - Option grouping for organization
 * - Layout utilities for positioning and spacing
 * - Automatic tracking ID generation for analytics
 *
 * @template T - The type of additional properties for items
 * @example
 * <Listbox selectionMode="single" onSelectionChange={handleSelection}>
 *   <Listbox.Option value="option1">Option 1</Listbox.Option>
 *   <Listbox.Option value="option2">Option 2</Listbox.Option>
 * </Listbox>
 */
export const Listbox = Object.assign(
  forwardRef(ListboxInner) as <T>(
    props: ListboxProps<T> & { ref?: React.ForwardedRef<HTMLUListElement> },
  ) => ReturnType<typeof ListboxInner>,
  {
    /**
     * ListboxOption component for individual selectable items within a listbox
     *
     * Features:
     * - Displays selectable options with proper ARIA attributes
     * - Supports disabled state for non-selectable options
     * - Keyboard navigation support
     * - Automatic tracking ID generation for analytics
     * - Accessible with screen reader support
     *
     * @example
     * <Listbox.Option value="option1" label="Option 1" />
     */
    Option: ListboxOption,
    /**
     * ListboxOptionGroup component for grouping related options within a listbox
     *
     * Features:
     * - Groups related options with a descriptive label
     * - Maintains proper ARIA structure for accessibility
     * - Supports keyboard navigation within groups
     * - Visual separation of option categories
     *
     * @example
     * <Listbox.OptionGroup label="Fruits">
     *   <Listbox.Option value="apple">Apple</Listbox.Option>
     *   <Listbox.Option value="banana">Banana</Listbox.Option>
     * </Listbox.OptionGroup>
     */
    OptionGroup: ListboxOptionGroup,
  },
);
